import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Plus, Building2, MapPin, Clock, Euro, Users, Archive, Edit3, Save, X,
  ArchiveRestore, Filter, AlertTriangle, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

import { apiFetch } from '../../services/api';
const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  name: '', address: '', client_name: '',
  planned_hours: '', budget_eur: '',
  start_date: '', end_date: '', notes: '',
  assigned_employee_ids: [] };

const fmtEUR = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(n || 0));

const progressColor = (pct) => {
  if (pct > 100) return { bar: 'bg-rose-500', text: 'text-rose-400', label: 'Überzogen' };
  if (pct >= 80) return { bar: 'bg-yellow-500', text: 'text-yellow-400', label: 'Achtung' };
  return { bar: 'bg-emerald-500', text: 'text-emerald-400', label: 'Im Plan' };
};

/**
 * Baustellen-Verwaltung (CRUD) für den Chef auf /pro/dashboard.
 *
 * - Card-Liste mit farbcodierter Fortschritts-Bar (Ist- vs. Soll-Stunden)
 * - Modal-Formular für Create + Edit
 * - Multi-Select der Monteure aus der Firmen-Employee-Liste
 * - Archivieren / Wiederherstellen statt Hard-Delete (Historie bleibt)
 */
const ProProjectsPanel = () => {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // Projekt-Objekt oder null (=create)
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, em] = await Promise.all([
        apiFetch(`${API}/api/pro/projects?include_archived=${showArchived}`, { headers: {} }).then((r) => r.json()),
        apiFetch(`${API}/api/pro/employees-lite`, { headers: {} }).then((r) => r.json()),
      ]);
      if (pr.success) setProjects(pr.projects || []);
      if (em.success) setEmployees(em.employees || []);
    } catch (err) {
      toast.error('Konnte Projekte nicht laden');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '', address: p.address || '', client_name: p.client_name || '',
      planned_hours: String(p.planned_hours ?? ''), budget_eur: String(p.budget_eur ?? ''),
      start_date: p.start_date || '', end_date: p.end_date || '', notes: p.notes || '',
      assigned_employee_ids: p.assigned_employee_ids || [] });
    setModalOpen(true);
  };

  const toggleEmp = (id) => {
    setForm((f) => {
      const set = new Set(f.assigned_employee_ids);
      set.has(id) ? set.delete(id) : set.add(id);
      return { ...f, assigned_employee_ids: Array.from(set) };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Projektname ist Pflicht');
    setSaving(true);
    const body = {
      name: form.name.trim(),
      address: form.address.trim(),
      client_name: form.client_name.trim(),
      planned_hours: Number(form.planned_hours) || 0,
      budget_eur: Number(form.budget_eur) || 0,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      notes: form.notes.trim(),
      assigned_employee_ids: form.assigned_employee_ids };
    try {
      const url = editing
        ? `${API}/api/pro/projects/${editing._id}`
        : `${API}/api/pro/projects`;
      const res = await apiFetch(url, {
        method: editing ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Speichern fehlgeschlagen');
      toast.success(editing ? 'Projekt aktualisiert' : 'Baustelle angelegt');
      setModalOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  const doArchive = async (p) => {
    if (!window.confirm(`Baustelle "${p.name}" archivieren?\n\nGebuchte Stunden bleiben erhalten – das Projekt verschwindet nur aus der aktiven Auswahl deiner Monteure.`)) return;
    try {
      const res = await apiFetch(`${API}/api/pro/projects/${p._id}/archive`, { method: 'POST', headers: {} });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Fehler');
      toast.success('Archiviert');
      loadAll();
    } catch (err) { toast.error(err.message || 'Fehler'); }
  };

  const doRestore = async (p) => {
    try {
      const res = await apiFetch(`${API}/api/pro/projects/${p._id}/restore`, { method: 'POST', headers: {} });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Fehler');
      toast.success('Wiederhergestellt');
      loadAll();
    } catch (err) { toast.error(err.message || 'Fehler'); }
  };

  const empMap = useMemo(() => Object.fromEntries(employees.map((e) => [e._id, e.name])), [employees]);

  return (
    <Card className="bg-gradient-to-br from-gray-950 to-black border-yellow-500/30" data-testid="pro-projects-panel">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="h-5 w-5 text-yellow-400" /> Baustellen-Verwaltung
            </CardTitle>
            <CardDescription className="text-gray-400">
              Alle aktiven Projekte auf einen Blick – mit Live-Stunden-Fortschritt
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => setShowArchived((v) => !v)}
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
              data-testid="toggle-archived"
            >
              <Filter className="h-4 w-4 mr-2" />
              {showArchived ? 'Nur aktive' : 'Archiv einblenden'}
            </Button>
            <Button
              size="sm"
              onClick={openCreate}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-semibold"
              data-testid="new-project-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Neue Baustelle
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 text-center text-gray-500">Lade Baustellen…</div>
        ) : projects.length === 0 ? (
          <div className="py-10 text-center space-y-3">
            <Building2 className="h-12 w-12 text-gray-700 mx-auto" />
            <p className="text-gray-400">Noch keine Baustellen angelegt.</p>
            <Button onClick={openCreate} className="bg-yellow-500 text-black font-semibold">
              <Plus className="h-4 w-4 mr-2" /> Erste Baustelle anlegen
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.map((p) => {
              const pct = Number(p.hours_pct || 0);
              const cap = Math.min(100, pct);
              const c = progressColor(pct);
              const archived = p.status === 'archived';
              return (
                <div
                  key={p._id}
                  className={`rounded-xl border p-4 space-y-3 transition-all ${
                    archived
                      ? 'border-gray-800 bg-black/20 opacity-70'
                      : 'border-gray-800 bg-black/40 hover:border-yellow-500/40'
                  }`}
                  data-testid={`project-card-${p._id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-white font-semibold truncate">{p.name}</h3>
                        {archived && <Badge variant="outline" className="border-gray-600 text-gray-500 text-[10px]">Archiviert</Badge>}
                      </div>
                      {p.address && <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" />{p.address}</p>}
                      {p.client_name && <p className="text-xs text-gray-500 mt-0.5">Kunde: {p.client_name}</p>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {!archived && (
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="text-gray-400 hover:text-yellow-400 h-8 w-8 p-0" data-testid={`edit-project-${p._id}`}>
                          <Edit3 className="h-4 w-4" />
                        </Button>
                      )}
                      {archived ? (
                        <Button size="sm" variant="ghost" onClick={() => doRestore(p)} className="text-gray-400 hover:text-emerald-400 h-8 w-8 p-0" data-testid={`restore-project-${p._id}`}>
                          <ArchiveRestore className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => doArchive(p)} className="text-gray-400 hover:text-rose-400 h-8 w-8 p-0" data-testid={`archive-project-${p._id}`}>
                          <Archive className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="bg-black/30 rounded p-2">
                      <p className="text-gray-500 flex items-center justify-center gap-1"><Clock className="h-3 w-3" /> Std.</p>
                      <p className="text-white font-semibold mt-1">{p.actual_hours || 0} / {p.planned_hours || 0}</p>
                    </div>
                    <div className="bg-black/30 rounded p-2">
                      <p className="text-gray-500 flex items-center justify-center gap-1"><Euro className="h-3 w-3" /> Budget</p>
                      <p className="text-white font-semibold mt-1">{fmtEUR(p.budget_eur)}</p>
                    </div>
                    <div className="bg-black/30 rounded p-2">
                      <p className="text-gray-500 flex items-center justify-center gap-1"><Users className="h-3 w-3" /> Team</p>
                      <p className="text-white font-semibold mt-1">{(p.assigned_employee_ids || []).length}</p>
                    </div>
                  </div>

                  {/* Stunden-Progress-Bar */}
                  <div>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="text-gray-400">Stunden-Fortschritt</span>
                      <span className={`font-semibold ${c.text}`}>
                        {pct.toFixed(1)}% · {c.label}
                        {pct > 100 && <AlertTriangle className="inline h-3 w-3 ml-1" />}
                        {pct < 80 && pct > 0 && <CheckCircle2 className="inline h-3 w-3 ml-1" />}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-900 rounded-full overflow-hidden relative">
                      <div
                        className={`h-full ${c.bar} rounded-full transition-all duration-500`}
                        style={{ width: `${cap}%` }}
                        data-testid={`progress-${p._id}`}
                      />
                      {pct > 100 && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-[9px] font-bold text-white/90">+{(pct - 100).toFixed(0)}% über Budget</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {(p.assigned_employee_ids || []).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {(p.assigned_employee_ids || []).slice(0, 4).map((id) => (
                        <span key={id} className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30">
                          {empMap[id] || 'Monteur'}
                        </span>
                      ))}
                      {(p.assigned_employee_ids || []).length > 4 && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">
                          +{(p.assigned_employee_ids || []).length - 4}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          className="bg-gray-950 border-yellow-500/40 max-w-2xl max-h-[92vh] overflow-y-auto"
          data-testid="project-form-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="h-5 w-5 text-yellow-500" />
              {editing ? 'Baustelle bearbeiten' : 'Neue Baustelle anlegen'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Kalkuliere Soll-Stunden &amp; Budget, weise Monteure zu.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300">Projektname / Bauvorhaben *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. PV-Anlage Familie Huber"
                className="bg-gray-900 border-gray-800 mt-1"
                data-testid="form-project-name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Baustellen-Adresse</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Straße, PLZ Ort"
                  className="bg-gray-900 border-gray-800 mt-1"
                  data-testid="form-project-address"
                />
              </div>
              <div>
                <Label className="text-gray-300">Kunde (optional)</Label>
                <Input
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  placeholder="Kundenname"
                  className="bg-gray-900 border-gray-800 mt-1"
                  data-testid="form-project-client"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Kalkulierte Stunden</Label>
                <Input
                  type="number" min="0" step="0.5"
                  value={form.planned_hours}
                  onChange={(e) => setForm({ ...form, planned_hours: e.target.value })}
                  placeholder="z.B. 80"
                  className="bg-gray-900 border-gray-800 mt-1"
                  data-testid="form-project-hours"
                />
              </div>
              <div>
                <Label className="text-gray-300">Material-Budget (€)</Label>
                <Input
                  type="number" min="0" step="100"
                  value={form.budget_eur}
                  onChange={(e) => setForm({ ...form, budget_eur: e.target.value })}
                  placeholder="z.B. 5000"
                  className="bg-gray-900 border-gray-800 mt-1"
                  data-testid="form-project-budget"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Start-Datum</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="bg-gray-900 border-gray-800 mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-300">Geplantes Ende</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="bg-gray-900 border-gray-800 mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-300 flex items-center gap-2 mb-2">
                <Users className="h-4 w-4" /> Monteure zuweisen ({form.assigned_employee_ids.length} ausgewählt)
              </Label>
              {employees.length === 0 ? (
                <p className="text-sm text-gray-500 bg-gray-900 border border-gray-800 rounded p-3">
                  Noch keine Mitarbeiter angelegt. Füge zunächst Monteure im Basis-Cockpit hinzu.
                </p>
              ) : (
                <div className="max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1 bg-gray-900 border border-gray-800 rounded-lg p-2" data-testid="form-employee-select">
                  {employees.map((e) => {
                    const active = form.assigned_employee_ids.includes(e._id);
                    return (
                      <button
                        key={e._id}
                        type="button"
                        onClick={() => toggleEmp(e._id)}
                        className={`text-left px-3 py-2 rounded transition-colors text-sm flex items-center gap-2 ${
                          active
                            ? 'bg-yellow-500/20 text-yellow-200 border border-yellow-500/40'
                            : 'text-gray-300 hover:bg-gray-800 border border-transparent'
                        }`}
                        data-testid={`emp-toggle-${e._id}`}
                      >
                        <span className={`w-4 h-4 rounded border-2 flex items-center justify-center ${active ? 'bg-yellow-500 border-yellow-500' : 'border-gray-600'}`}>
                          {active && <CheckCircle2 className="h-3 w-3 text-black" />}
                        </span>
                        <span className="truncate">{e.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <Label className="text-gray-300">Notizen</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                placeholder="Interne Notizen, Sonderabsprachen…"
                className="bg-gray-900 border-gray-800 mt-1"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-800">
              <Button
                variant="outline"
                onClick={() => setModalOpen(false)}
                disabled={saving}
                className="border-gray-700 text-gray-200"
                data-testid="form-cancel"
              >
                <X className="h-4 w-4 mr-2" /> Abbrechen
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-semibold"
                data-testid="form-save"
              >
                <Save className={`h-4 w-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
                {saving ? 'Speichern…' : (editing ? 'Änderungen speichern' : 'Baustelle anlegen')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ProProjectsPanel;
